import { HttpDataService, IHttpDataCache } from '../http/http-data.service';
import { ICountryExtended, IGenericUser, INamedObject, INamedStringObject, IObject, ISubCompany, IWebshop } from '../../../type_definitions/datatypes';
import { Injectable } from '@angular/core';
import { IUserWebshop } from '../services/auth/auth-data.service';
import { IDualMultiSelectElement } from '../dual-multi-select/dual-multi-select.component';

export interface IPermissionCountryLocation {
  country:INamedObject;
  location:INamedObject[];
}

export interface IPermissionTypeACSL {
  workflowType:'PO' | 'PQ';
  create:IPermissionCountryLocation[];
  viewOwn:IPermissionCountryLocation[];
  viewOthers:IPermissionCountryLocation[];
}

export interface ICustomerPermission {
  countries:ICountryExtended[];
  regions:INamedStringObject[];
  webshops:IWebshop[];
  subCompanies?:ISubCompany[];
  canPlaceOrder:boolean;
  canPlaceQuote:boolean;
  viewOnlyOwnOrder:boolean;
  viewOnlyOwnQuote:boolean;
  viewOthersOrder:boolean;
  viewOthersQuote:boolean;
}

export interface ICustomerPermissionExtended extends ICustomerPermission {
  canPlaceText?:string;
  canViewOthersText?:string;
  canViewOwnText?:string;
}

export interface IWebshopPermissionResponse {
  userType:string;
  permissionToAllWebshops:boolean;
  selectedWebshops:INamedObject[];
  possibleWebshops:INamedObject[];
}

export interface IOrderQuotePermissionResponse {
  userType:string;
  permissionTypes?:IPermissionTypeACSL[];
  customerPermission?:ICustomerPermission;
}

export interface IWebshopPermission {
  userType:string;
  permissionToAllWebshops:IWebshopPermissionSelector;
  selectedWebshops:IDualMultiSelectElement[];
  possibleWebshops:IDualMultiSelectElement[];
}

export interface IWebshopPermissionSelector {
  id:boolean;
  description?:string;
}

export interface IACSLPermission {
  order:IPermissionTypeACSL;
  quote:IPermissionTypeACSL;
}

@Injectable()
export class UserDataService {
  private cache:IHttpDataCache<any>;

  constructor(
    private httpDataService:HttpDataService
  ) {
    this.cache = httpDataService.createCache();
  }

  public clear():void {
    this.cache.clear();
  }

  public createSignupLink(companyId?:number, subcompanyId?:number, webshopId?:number):Promise<string> {
    return this.httpDataService.doGET(
      'rest/userman/signuplink',
      {
        companyId,
        subcompanyId,
        webshopId
      },
      undefined,
      'Error creating signup link'
    );
  }

  public acceptPrivacyStatement():Promise<IObject> {
    return this.httpDataService.doPOST(
      'rest/userman/agreeprivacy',
      undefined,
      undefined,
      undefined,
      'Error saving privacy settings'
    );
  }

  public getGenericUsers(companyId?:number, webshopId?:number):Promise<IGenericUser[]> {
    return this.cache.doGET(
      `rest/userman/userprofile/genericusers`,
      {
        companyId,
        webshopId
      },
      undefined,
      'Error loading generic users'
    );
  }

  public getPermissions(userId:number):Promise<IWebshopPermissionResponse | IOrderQuotePermissionResponse> {
    return this.httpDataService.doGET(
      `rest/userman/userprofile/${userId}/permissions`,
      {},
      undefined,
      'Error loading permissions'
    );
  }

  public checkEmailSuffix(companyType:string, companyId:number, email:string):Promise<{ validSuffix:boolean }> {
    return this.httpDataService.doGET(
      `rest/userman/validateemailsuffix`,
      {
        companyType,
        companyId,
        email
      },
      undefined,
      'Error validating e-mail suffix'
    );
  }

  public getSpendLimit(userId:number): Promise<IUserWebshop[]> {
    return this.httpDataService.doGET(
      `rest/userman/userprofile/${userId}/spendlimit`,
      {},
      undefined,
      'Error loading spend limit'
    );
  }
}